import { useQueryClient } from '@tanstack/react-query';
import type { Table } from '@tanstack/react-table';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { GET_PURCHASE_ORDER_SYNCABLE_FIELDS_KEY } from '@/keys';
import { useSyncPurchaseOrderLines } from '@/services/apis/mutations/purchase-orders/use-sync-purchase-order-lines';
import { useGetPurchaseOrder } from '@/services/apis/queries/purchase-order/use-get-purchase-order';
import type { PurchaseOrderProduct } from '@/types/core/purchase-order/purchase-order-product';
import type { PurchaseOrderSyncableField } from '@/types/core/purchase-order/purchase-order-syncable-field';

type UsePurchaseOrderSyncInformationsButtonParams = {
  purchaseOrderId: string;
  table: Table<PurchaseOrderProduct>;
  totalProductsCount: number;
};

export function usePurchaseOrderSyncInformationsButton({
  purchaseOrderId,
  table,
  totalProductsCount,
}: UsePurchaseOrderSyncInformationsButtonParams) {
  const { t } = useTranslation('purchaseOrder');
  const { data: purchaseOrder } = useGetPurchaseOrder(purchaseOrderId);
  const queryClient = useQueryClient();
  const selectedProductsCount = table.getSelectedRowModel().rows.length;
  const hasSelectedProducts = selectedProductsCount > 0;
  const [isSyncDialogOpen, setIsSyncDialogOpen] = useState(false);
  const [selectedSyncFieldKeys, setSelectedSyncFieldKeys] = useState<string[]>(
    [],
  );
  const [pendingSyncProductIds, setPendingSyncProductIds] = useState<
    number[] | null
  >(null);
  const {
    mutate: syncPurchaseOrderLines,
    isPending: isSyncingPurchaseOrderLines,
  } = useSyncPurchaseOrderLines();
  const isSyncEnabled =
    purchaseOrder.isEligibleForEditionOrSync &&
    table.getRowCount() > 0 &&
    !isSyncingPurchaseOrderLines;

  const onSyncInformationsClick = useCallback(() => {
    const selectedProductIds = table
      .getSelectedRowModel()
      .rows.map((row) => row.original.product.id);

    setPendingSyncProductIds(
      selectedProductIds.length > 0 ? selectedProductIds : null,
    );
    setSelectedSyncFieldKeys([]);
    setIsSyncDialogOpen(true);
  }, [table]);

  const onSyncInformationsConfirm = useCallback(() => {
    const syncableFields =
      queryClient.getQueryData<PurchaseOrderSyncableField[]>(
        GET_PURCHASE_ORDER_SYNCABLE_FIELDS_KEY,
      ) ?? [];
    const syncableFieldKeys = new Set(syncableFields.map((field) => field.key));
    const syncFieldKeys = selectedSyncFieldKeys.filter((fieldKey) =>
      syncableFieldKeys.has(fieldKey),
    );

    if (syncFieldKeys.length === 0) {
      return;
    }

    syncPurchaseOrderLines(
      {
        id: purchaseOrderId,
        productIds: pendingSyncProductIds,
        syncFieldKeys,
      },
      {
        onSuccess: (result) => {
          if (result && result.skippedProductHsCodeLines.length > 0) {
            toast.warning(t('syncInformationsHsCodeSkipped'), {
              description: t('syncInformationsHsCodeSkippedDescription', {
                skus: result.skippedProductHsCodeLines
                  .map(
                    (line) =>
                      line.sku ??
                      t('syncInformationsLineId', { id: line.lineId }),
                  )
                  .join(', '),
              }),
              duration: Number.POSITIVE_INFINITY,
              closeButton: true,
              classNames: {
                description: 'max-h-60 overflow-y-auto',
              },
            });
          } else {
            toast.success(t('syncInformationsSuccess'));
          }
          setIsSyncDialogOpen(false);
        },
        onError: () => {
          toast.error(t('error', { ns: 'common' }), {
            description: t('syncInformationsError'),
          });
        },
      },
    );
  }, [
    pendingSyncProductIds,
    purchaseOrderId,
    queryClient,
    selectedSyncFieldKeys,
    syncPurchaseOrderLines,
    t,
  ]);

  return {
    isSyncEnabled,
    data: {
      purchaseOrderId,
      counts: {
        hasSelectedProducts,
        selectedProductsCount,
        totalProductsCount,
      },
      dialog: {
        isOpen: isSyncDialogOpen,
        isSubmitting: isSyncingPurchaseOrderLines,
        selectedFieldKeys: selectedSyncFieldKeys,
        setSelectedFieldKeys: setSelectedSyncFieldKeys,
        onOpenChange: setIsSyncDialogOpen,
        onOpen: onSyncInformationsClick,
        onConfirm: onSyncInformationsConfirm,
      },
    },
  };
}
